// src/contexts/PlayerContext.jsx
import React, { createContext, useContext, useReducer, useCallback } from 'react';

export const PlayerContext = createContext();
export const StreamStatus = {
  CONNECTING: 'connecting',
  ONLINE: 'online',
  OFFLINE: 'offline',
  ERROR: 'error'
};

// Simplified state - no more stream URL management
const initialState = {
  isPlaying: false,
  isVideo: true, // Always true for live stream
  volume: 1,
  muted: false,
  currentTrack: null,
  mode: 'live', // Default to live mode
  streamStatus: StreamStatus.OFFLINE,
};

function playerReducer(state, action) {
  switch (action.type) {
    case 'PLAY':
      return { ...state, isPlaying: true };
    case 'PAUSE':
      return { ...state, isPlaying: false };
    case 'STOP':
      return { ...state, isPlaying: false };
    case 'SET_VOLUME':
      return { ...state, volume: action.payload };
    case 'TOGGLE_MUTE':
      return { ...state, muted: !state.muted };
    case 'SET_STREAM_STATUS':
      return { ...state, streamStatus: action.payload };
    case 'SET_TRACK': // For VOD
      return {
        ...state,
        currentTrack: action.payload,
        isVideo: false,
        mode: 'vod'
      };
    case 'SET_LIVE_STREAM': // For Live Stream
      return {
        ...state,
        currentTrack: action.payload,
        isVideo: true,
        mode: 'live'
      };
    default:
      return state;
  }
}

export const PlayerProvider = ({ children }) => {
  const [state, dispatch] = useReducer(playerReducer, initialState);

  const playTrack = useCallback((track) => {
    if (!track?.audioUrl) {
      console.error('PLAYER: No audio URL provided for track');
      return;
    }

    console.log('PLAYER: Playing track:', track.title);
    dispatch({ type: 'PAUSE' });
    setTimeout(() => {
      dispatch({ type: 'SET_TRACK', payload: track });
      dispatch({ type: 'PLAY' });
    }, 50);
  }, []);

  // Prepare the live stream without playing it
  const prepareLiveStream = useCallback((streamUrl) => {
    if (!streamUrl) {
      console.error('PLAYER: No stream URL provided');
      return;
    }

    console.log('PLAYER: Preparing live stream');
    dispatch({ type: 'PAUSE' });
    setTimeout(() => {
      dispatch({ 
        type: 'SET_LIVE_STREAM', 
        payload: {
          id: 'live-stream',
          title: 'שידור חי',
          audioUrl: streamUrl,
          mode: 'live'
        } 
      });
      // Don't play automatically - just prepare
    }, 50);
  }, []);

  // Play the live stream (will first prepare it)
  const playLiveStream = useCallback((streamUrl) => {
    if (!streamUrl) {
      console.error('PLAYER: No stream URL provided');
      return;
    }

    console.log('PLAYER: Playing live stream');
    dispatch({ type: 'PAUSE' });
    setTimeout(() => {
      dispatch({ 
        type: 'SET_LIVE_STREAM', 
        payload: {
          id: 'live-stream',
          title: 'שידור חי',
          audioUrl: streamUrl,
          mode: 'live'
        } 
      });
      dispatch({ type: 'PLAY' });
    }, 50);
  }, []);

  const stop = useCallback(() => {
    console.log('PLAYER: Stopping playback');
    dispatch({ type: 'PAUSE' });
    setTimeout(() => {
      dispatch({ type: 'STOP' });
    }, 50);
  }, []);

  const pause = useCallback(() => {
    console.log('PLAYER: Pausing playback');
    dispatch({ type: 'PAUSE' });
  }, []);

  const play = useCallback(() => {
    console.log('PLAYER: Starting playback');
    
    // If already playing, don't do anything
    if (state.isPlaying) {
      console.log('PLAYER: Already playing');
      return;
    }
    
    // Start playing
    dispatch({ type: 'PLAY' });
  }, [state.isPlaying]);

  const setVolume = useCallback((vol) => {
    dispatch({ type: 'SET_VOLUME', payload: vol });
  }, []);

  const toggleMute = useCallback(() => {
    dispatch({ type: 'TOGGLE_MUTE' });
  }, []);

  const value = {
    state,
    dispatch,
    play,
    pause,
    stop,
    setVolume,
    toggleMute,
    playTrack,
    playLiveStream,
    prepareLiveStream,
    currentTrack: state.currentTrack
  };

  return (
    <PlayerContext.Provider value={value}>
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
};
